import { useRouter } from 'next/router'
import { useSession } from 'next-auth/react'
import useTranslation from 'next-translate/useTranslation'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast, ToastContainer } from 'react-toastify'

import { getDnsPlans } from '@/api/dns/getDnsPlans'
import { orderItem } from '@/api/order/orderItem'
import ErrorCard from '@/components/ui/ErrorCard'
import Spinner from '@/components/ui/Spinner'
import TextRow from '@/components/ui/TextRow'
import ButtonText from '@/components/ui/Typo/ButtonText'
import H2 from '@/components/ui/Typo/H2'
import Subtitle from '@/components/ui/Typo/Subtitle'
import { IconArrowRight, IconSearch } from '@/res/icons'
import { NAMESPACE_DOMAIN } from '@/res/namespaces'
import {
  ROUTE_IDENTIFICATION,
  ROUTE_LOGIN,
  ROUTE_ORDER,
  ROUTE_SETTINGS,
} from '@/res/routes'
import { IDomainResponse } from '@/types/Domain'
import { IHostingPlan } from '@/types/Hosting'
import { numberWithSpaces } from '@/utils/numberWithSpaces'

interface Props {
  domain: string
  data?: IDomainResponse | null
  isError?: boolean
  isForbidden?: boolean
}

const DomainRegister: React.FC<Props> = ({
  domain,
  isError,
  data,
  isForbidden,
}) => {
  const { t } = useTranslation(NAMESPACE_DOMAIN)
  const [domainLoading, setDomainLoading] = useState(false)

  const [dnsPlan, setDnsPlan] = useState<null | IHostingPlan>(null)
  const router = useRouter()
  const session = useSession()
  const [price, setPrice] = useState<string | null>(null)

  const { handleSubmit, register } = useForm({
    defaultValues: {
      domain,
    },
  })

  useEffect(() => {
    const handleRouteChange = () => setDomainLoading(true)
    const handleRouteComplete = () => setDomainLoading(false)
    router.events.on('routeChangeStart', handleRouteChange)
    router.events.on('routeChangeComplete', handleRouteComplete)

    return () => {
      router.events.off('routeChangeStart', () => handleRouteChange)
      router.events.off('routeChangeStart', () => handleRouteComplete)
    }
  })

  useEffect(() => {
    const getPlan = async () => {
      try {
        const res = await getDnsPlans()
        if (res.length) {
          const plan = res[0]
          setDnsPlan(plan)
          setPrice(plan.price)
        }
      } catch (err) {
        //eslint-disable-next-line
        console.log(err)
      }
    }
    if (!dnsPlan) {
      getPlan()
    }
  }, [dnsPlan])

  const onSubmit = handleSubmit(async ({ domain }) => {
    if (router.query.domain_name === domain) {
      return
    }
    if (!domain.includes('.')) {
      router.push(`/services/dns/${domain}.uz`)
    } else {
      router.push(`/services/dns/${domain}`)
    }
  })

  const onError = () =>
    toast.error('Что-то пошло не так, попробуйте обновить страницу.')

  const onOrderDomain = async () => {
    try {
      const user = session.data?.user
      if (!user) {
        router.push(
          {
            pathname: ROUTE_LOGIN,
            query: {
              from: router.asPath,
            },
          },
          undefined
        )
      } else if (!user.is_passport_verified) {
        router.push({
          pathname: ROUTE_IDENTIFICATION,
          query: {
            show_info: true,
          },
        })
      } else if (!user.phone_number) {
        router.push({
          pathname: ROUTE_SETTINGS,
          query: {
            show_info: true,
          },
        })
      } else if (dnsPlan?.id) {
        const res = await orderItem(
          { domain, plan: dnsPlan.id },
          session.data?.user.accessToken as string
        )
        if (res) {
          router.push(ROUTE_ORDER)
        }
      } else {
        Promise.reject('error')
      }
    } catch {
      onError()
    }
  }

  const submitBtn = (
    <button
      type="submit"
      className="px-4 py-3 transition-opacity rounded-lg bg-primary hover:opacity-90"
    >
      <ButtonText size="big">{t('register_form.pick_up')}</ButtonText>
    </button>
  )

  return (
    <>
      <div className="container mt-4 mb-20">
        <div className="p-5 border border-gray-300 rounded-lg small:p-10">
          <H2
            weight="font-semibold"
            uppercase={false}
            color="text-primary"
            className="mb-6 small:mb-10"
          >
            {t('register_form.register_domain')}
          </H2>
          <form onSubmit={onSubmit}>
            <div className="relative">
              <IconSearch className="absolute text-gray-600 -translate-y-1/2 left-3 top-1/2" />
              <input
                {...register('domain')}
                required
                placeholder={t('register_form.placeholder')}
                autoCorrect="off"
                autoComplete="off"
                className="w-full py-3 pl-12 pr-4 text-xl font-medium border-2 border-gray-400 rounded-md outline-none focus:border-primary placeholder:font-normal placeholder:text-lg"
              />
              <div
                className={`small:block hidden absolute ${
                  domainLoading ? 'right-3' : 'right-1.5'
                } top-1/2 -translate-y-1/2`}
              >
                {domainLoading ? <Spinner color="border-primary" /> : submitBtn}
              </div>
            </div>
            <div className="flex justify-center mt-3 small:hidden">
              {submitBtn}
            </div>
          </form>
          <div className="mt-6 small:mt-8">
            {isForbidden ? (
              <ErrorCard text={t('register_form.forbidden')} />
            ) : isError ? (
              <ErrorCard text={t('register_form.domain_not_found')} />
            ) : data ? (
              <>
                <ErrorCard
                  className="mb-3"
                  text={t('register_form.domain_is_busy')}
                />
                <div className="overflow-hidden border border-gray-300 rounded-lg">
                  <Subtitle className="p-6">
                    {t('register_form.domain_info')}
                  </Subtitle>
                  <TextRow
                    title={t('register_form.domain_name')}
                    text={data.dName}
                  />
                  {data.sOrg !== 'not.defined.' && (
                    <TextRow
                      title={t('register_form.organization')}
                      text={data?.sOrg}
                    />
                  )}
                  <TextRow
                    title={t('register_form.registrator')}
                    text={data.sRegistrator}
                  />
                  <TextRow
                    title={t('register_form.customer')}
                    text={data.sCustomer}
                  />
                  <TextRow
                    title={t('register_form.status')}
                    text={data.dStatus}
                  />
                  {data.sCustomerPhone && (
                    <TextRow
                      title={t('register_form.customer_phone')}
                      text={data.sCustomerPhone}
                    />
                  )}
                  {data.sCustomerEMail && (
                    <TextRow
                      title={t('register_form.customer_email')}
                      text={data.sCustomerEMail}
                    />
                  )}
                  {data.sCustomerAddress && (
                    <TextRow
                      title={t('register_form.customer_address')}
                      text={data.sCustomerAddress}
                      withoutBorder
                    />
                  )}
                </div>
              </>
            ) : domain ? (
              <div>
                <H2
                  uppercase={false}
                  className="!text-[#364364] mb-4"
                  weight="font-semibold"
                >
                  {domain}
                </H2>
                <Subtitle weight="font-medium">
                  <span className="text-[#00c084]">
                    {t('register_form.domain_is_free')},
                  </span>
                  <span className="ml-1 font-semibold">
                    {price ? numberWithSpaces(parseFloat(price)) + ' сум' : ''}
                  </span>{' '}
                  {t('register_form.register_now')}
                </Subtitle>
                <button
                  onClick={onOrderDomain}
                  className="flex items-center p-3 mt-5 transition-all rounded bg-green-600/90 hover:bg-green-600 active:scale-x-95"
                >
                  <ButtonText size="big" className="mr-2">
                    {t('register_form.register')}
                  </ButtonText>
                  <IconArrowRight />
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
      <ToastContainer />
    </>
  )
}

export default DomainRegister
